import { useEffect, useRef, useState } from "react";
import { Loader2, Send, X, Sparkles, MapPin, Car } from "lucide-react";
import { useOndi } from "../../context/OndiContext";
import { OndiCharacter } from "./OndiCharacter";
import { unlockAudio } from "../../lib/voice";

export function OndiPanel({ anchor }) {
  const { panelOpen, closePanel, history, thinking, sendMessage, startTour, greeting, openDriving } = useOndi();
  const [text, setText] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!panelOpen) return;
    setTimeout(() => inputRef.current?.focus(), 80);
  }, [panelOpen]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, thinking, panelOpen]);

  if (!panelOpen) return null;

  const submit = async (e) => {
    e && e.preventDefault();
    const v = text.trim();
    if (!v || thinking) return;
    setText("");
    await sendMessage(v);
  };

  // FIX 2 — Do NOT dismiss the panel via scrim while ONDI is thinking. This
  // prevents accidental cancellation of an in-flight reply. Explicit close
  // (X button, "Ahora no", or Escape) is still allowed.
  const handleScrimClick = () => {
    if (thinking) return;
    closePanel();
  };

  const showTourCTA =
    greeting && !greeting.has_seen_tour && history.length <= 2 && history[history.length - 1]?.role === "assistant";

  return (
    <>
      <div
        className="fixed inset-0 z-[59] bg-black/30 backdrop-blur-[2px] sm:bg-transparent sm:backdrop-blur-0 sm:pointer-events-none"
        onClick={handleScrimClick}
        data-testid="ondi-panel-scrim"
        data-thinking={thinking ? "true" : "false"}
        aria-hidden="true"
      />
      <div
        className="fixed z-[60] q-glass rounded-3xl overflow-hidden border border-white/10 shadow-2xl q-page-enter flex flex-col"
        style={panelStyle(anchor)}
        data-testid="ondi-panel"
        role="dialog"
        aria-label="Chat con ONDI"
      >
        <header className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
          <OndiCharacter size={40} waving />
          <div className="flex-1 min-w-0">
            <div className="font-display text-base font-bold leading-none">
              ONDI <span className="q-accent-text text-xs uppercase tracking-widest ml-1">tu guía</span>
            </div>
            <div className="text-[10px] text-slate-400 font-mono uppercase tracking-widest">
              {thinking ? "ONDI está pensando…" : "listo para ayudar"}
            </div>
          </div>
          <button
            type="button"
            className="text-slate-300 hover:text-slate-100 p-1.5 rounded-lg border border-white/10 inline-flex items-center gap-1 text-xs"
            onClick={() => {
              try {
                unlockAudio();
              } catch (_e) {
                /* ignore */
              }
              openDriving();
            }}
            aria-label="Activar Modo Conducción"
            data-testid="ondi-driving-open"
            title="Modo Conducción (manos libres)"
          >
            <Car size={14} />
            <span className="hidden sm:inline">Conducción</span>
          </button>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-100 p-1"
            onClick={closePanel}
            aria-label="Cerrar"
            data-testid="ondi-close"
          >
            <X size={18} />
          </button>
        </header>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto no-scrollbar px-4 py-3 space-y-2 bg-transparent"
          data-testid="ondi-messages"
        >
          {history.length === 0 && (
            <div className="text-xs text-slate-400">
              Pregúntame lo que sea sobre la app. Estoy aquí para orientarte.
            </div>
          )}
          {history.map((m, i) => (
            <div
              key={i}
              className={"flex " + (m.role === "user" ? "justify-end" : "justify-start")}
              data-testid={`ondi-msg-${m.role}-${i}`}
            >
              <div
                className={
                  "max-w-[86%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words " +
                  (m.role === "user"
                    ? "rounded-br-sm text-[color:var(--accent-contrast)]"
                    : "rounded-bl-sm bg-white/[0.06] border border-white/10 text-slate-100")
                }
                style={m.role === "user" ? { background: "var(--accent)" } : undefined}
              >
                {m.content}
              </div>
            </div>
          ))}
          {thinking && (
            <div className="flex justify-start" data-testid="ondi-thinking">
              <div className="rounded-2xl px-3 py-2 text-sm bg-white/[0.06] border border-white/10 text-slate-300 flex items-center gap-2">
                <Loader2 size={14} className="animate-spin q-accent-text" />
                ONDI está pensando…
              </div>
            </div>
          )}

          {showTourCTA && (
            <div className="flex gap-2 pt-1" data-testid="ondi-tour-cta">
              <button
                type="button"
                onClick={startTour}
                className="q-btn-primary inline-flex items-center gap-1 !py-1.5 !px-3 text-xs"
                data-testid="ondi-tour-start"
              >
                <MapPin size={12} /> Empezar tour
              </button>
              <button
                type="button"
                onClick={closePanel}
                className="q-btn-ghost inline-flex items-center gap-1 !py-1.5 !px-3 text-xs"
                data-testid="ondi-tour-later"
              >
                Ahora no
              </button>
            </div>
          )}
        </div>

        <form onSubmit={submit} className="border-t border-white/10 p-2 flex items-center gap-2">
          <input
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={thinking ? "ONDI está pensando… puedes escribir el siguiente" : "Pregúntame algo…"}
            className="q-input flex-1 !py-2 text-sm"
            maxLength={800}
            data-testid="ondi-input"
          />
          <button
            type="submit"
            className="q-btn-primary !py-2 !px-3 inline-flex items-center gap-1"
            disabled={thinking || !text.trim()}
            data-testid="ondi-send"
            aria-label="Enviar"
          >
            {thinking ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </form>

        <div className="px-3 pb-2 flex items-center justify-between text-[10px] text-slate-500 font-mono uppercase tracking-widest">
          <span className="inline-flex items-center gap-1">
            <Sparkles size={10} /> gpt-4o-mini
          </span>
          <span>Historial local (últimos 20)</span>
        </div>
      </div>
    </>
  );
}

function panelStyle(anchor) {
  const w = 340;
  const h = 460;
  const pad = 16;
  const vw = typeof window !== "undefined" ? window.innerWidth : 400;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const isMobile = vw < 640;
  if (isMobile) {
    return {
      left: 8,
      right: 8,
      bottom: 8,
      maxHeight: "min(80dvh, 640px)",
      height: "min(72dvh, 620px)",
    };
  }
  // Desktop: anchor near the character
  const ax = anchor?.x ?? vw - 96;
  const ay = anchor?.y ?? vh - 96;
  const preferLeft = ax + w + pad > vw;
  const preferTop = ay - h - pad > 0;
  return {
    width: w,
    height: h,
    left: preferLeft ? Math.max(pad, ax - w - pad) : Math.min(vw - w - pad, ax + 96),
    top: preferTop ? Math.max(pad, ay - h - pad) : Math.min(vh - h - pad, ay + 96),
  };
}
